import {
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    Tab,
    Tabs,
    IconButton,
    Box,
} from '@mui/material';
import {
    Add, Close,
} from '@mui/icons-material';
import { useState } from 'react';
import { OptionGroupType } from 'src/constants';
import { OptionGroupDetails } from './OptionGroupDetails';
import { OptionGroupsList } from './OptionGroupsList';

interface ManageOptionGroupsDialogProps {
    open: boolean;
    onClose: () => void;
    filteringPartialProductSpecs: Record<string, Models.IngestConfigApi.ForcedRankings.PartialProductSpec>;
    defaultPartialProductSpecs: Record<string, Models.IngestConfigApi.ForcedRankings.PartialProductSpec>;
    addOptionGroup: (
        groupType: OptionGroupType,
        id: string,
        spec: Models.IngestConfigApi.ForcedRankings.PartialProductSpec
    ) => void;
    updateOptionGroup: (
        groupType: OptionGroupType,
        id: string,
        update: Models.IngestConfigApi.ForcedRankings.PartialProductSpec
    ) => void;
    deleteOptionGroup: (groupType: OptionGroupType, id: string) => void;
    productKey: string;
}

export const ManageOptionGroupsDialog = ({
    open,
    onClose,
    filteringPartialProductSpecs,
    defaultPartialProductSpecs,
    addOptionGroup: _addOptionGroup,
    updateOptionGroup,
    deleteOptionGroup,
    productKey,
}: ManageOptionGroupsDialogProps): JSX.Element => {
    const [tab, setTab] = useState(0);
    const [isList, setIsList] = useState(true);
    const [isEditingExistingGroup, setIsEditingExistingGroup] = useState(true);
    const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
    const [selectedPartialProductSpec, setSelectedPartialProductSpec] = useState<
    Models.IngestConfigApi.ForcedRankings.PartialProductSpec | null
    >(null);

    const handleTabChange = (_: React.SyntheticEvent, newValue: number): void => {
        setTab(newValue);
    };

    const handleOpenDetails = (id: string | null, isEditing: boolean): void => {
        setSelectedGroupId(id);
        if (id) {
            setSelectedPartialProductSpec(tab === 0
                ? filteringPartialProductSpecs[id]
                : defaultPartialProductSpecs[id]);
        }
        setIsEditingExistingGroup(isEditing);
        setIsList(false);
    };

    const handleCloseDetails = (): void => {
        setIsList(true);
        setSelectedGroupId(null);
        setSelectedPartialProductSpec(null);
    };

    return (
        <Dialog
            fullWidth
            maxWidth="md"
            open={open}
            sx={{
                '& .MuiDialog-paper': {
                    height: '80vh',
                    maxHeight: '80vh',
                },
            }}
            onClose={onClose}
        >
            {isList ? (
                <>
                    <DialogTitle>Manage Option Groups</DialogTitle>
                    <IconButton
                        aria-label="close"
                        sx={(theme): any => ({
                            position: 'absolute',
                            right: theme.spacing(2),
                            top: theme.spacing(2),
                            color: theme.palette.grey[500],
                        })}
                        onClick={onClose}
                    >
                        <Close />
                    </IconButton>
                    <DialogContent sx={{ height: 'calc(100% - 120px)', p: 6, pt: 0 }}>
                        <Tabs
                            sx={{ borderBottom: 1, borderColor: 'divider' }}
                            value={tab}
                            variant="fullWidth"
                            onChange={handleTabChange}
                        >
                            <Tab label="Filtering Option Groups" />
                            <Tab label="Default Option Groups" />
                        </Tabs>
                        <Box sx={{
                            mt: 3, height: 'calc(100% - 48px)', overflow: 'auto',
                        }}
                        >
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                                <Button
                                    endIcon={<Add />}
                                    variant="outlined"
                                    onClick={(): void => handleOpenDetails(null, false)}
                                >
                                    Create group
                                </Button>
                            </Box>
                            {tab === 0 ? (
                                <OptionGroupsList
                                    options={filteringPartialProductSpecs}
                                    type={OptionGroupType.FILTERING}
                                    onDelete={deleteOptionGroup}
                                    onEdit={(id): void => handleOpenDetails(id, true)}
                                />
                            ) : (
                                <OptionGroupsList
                                    options={defaultPartialProductSpecs}
                                    type={OptionGroupType.DEFAULT}
                                    onDelete={deleteOptionGroup}
                                    onEdit={(id): void => handleOpenDetails(id, true)}
                                />
                            )}
                        </Box>
                    </DialogContent>
                </>
            ) : (
                <OptionGroupDetails
                    addOptionGroup={_addOptionGroup}
                    currentGroupSpec={selectedPartialProductSpec}
                    groupId={selectedGroupId}
                    groupType={tab === 0 ? OptionGroupType.FILTERING : OptionGroupType.DEFAULT}
                    isEditingExistingGroup={isEditingExistingGroup}
                    productKey={productKey}
                    updateOptionGroup={updateOptionGroup}
                    onDone={handleCloseDetails}
                />
            )}
        </Dialog>
    );
};
